import { motion } from "framer-motion";
import { Minus, Plus } from "lucide-react";

const Field = ({ label, children, hint }) => (
  <label className="field block relative">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-stone-500">{hint}</span>}
    <span aria-hidden="true" className="field-line" />
  </label>
);

const Stepper = ({ value, min, max, onChange, testId }) => (
  <div className="mt-2 inline-flex items-center border border-stone-300 bg-white" data-testid={testId}>
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))} className="w-11 h-11 flex items-center justify-center hover:bg-stone-100" aria-label="Decrease" data-testid={`${testId}-minus`}><Minus size={14} /></button>
    <span className="w-10 text-center font-mono text-sm tabular-nums" data-testid={`${testId}-value`}>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))} className="w-11 h-11 flex items-center justify-center hover:bg-stone-100" aria-label="Increase" data-testid={`${testId}-plus`}><Plus size={14} /></button>
  </div>
);

const Toggle = ({ on, onClick, label, sub, testId }) => (
  <motion.button type="button" aria-pressed={on} onClick={onClick} whileTap={{ scale: 0.97 }} data-testid={testId}
    className={`text-left px-4 py-3 border transition-colors duration-300 ${on ? "bg-stone-900 text-[#FBFBF9] border-stone-900" : "border-stone-300 hover:border-stone-900"}`}>
    <span className="block font-mono text-xs uppercase tracking-[0.12em]">{on ? "— " : "+ "}{label}</span>
    <span className={`block text-[11px] mt-0.5 ${on ? "text-stone-300" : "text-stone-500"}`}>{sub}</span>
  </motion.button>
);

export const LocationStep = ({ rates, form, setForm }) => {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const kerala = form.region === "kerala";
  const loc = kerala ? rates.kerala.find((l) => l.id === form.location_id) : rates.outstate.find((l) => l.id === form.location_id);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-2" role="radiogroup" data-testid="visit-region-group">
        {[["kerala", "Kerala & Mahe", "Fixed fee by district, we drive from Thalassery"], ["outstate", "Other states / UTs", "Per-day zone fee, 50% advance now"]].map(([id, l, sub]) => (
          <motion.button key={id} type="button" role="radio" aria-checked={form.region === id} whileTap={{ scale: 0.97 }} data-testid={`visit-region-${id}`}
            onClick={() => setForm((f) => ({ ...f, region: id, location_id: id === "kerala" ? "kozhikode" : rates.outstate[0].id, days: 0, nights: 0, urgent: false, repeat: false }))}
            className={`text-left p-4 border transition-colors duration-300 ${form.region === id ? "border-stone-900 bg-white" : "border-stone-300 hover:border-stone-500"}`}>
            <span className="block font-serif text-lg text-stone-900">{l}</span>
            <span className="block text-[11px] text-stone-500 mt-1">{sub}</span>
          </motion.button>
        ))}
      </div>

      <Field label={kerala ? "District" : "State / Union Territory"} hint={loc && (kerala ? `${loc.hq} · about ${loc.km} km one way` : `Zone ${loc.zone} — ${loc.zone_label} · ₹${loc.per_day.toLocaleString("en-IN")} per day · minimum ${loc.min_days} days${loc.note ? ` · ${loc.note}` : ""}`)}>
        <select value={form.location_id} onChange={(e) => set("location_id", e.target.value)} className="mt-2 w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900" data-testid="visit-location-select">
          {(kerala ? rates.kerala : rates.outstate).map((l) => (
            <option key={l.id} value={l.id}>{l.name}{kerala ? ` — ₹${l.charge.toLocaleString("en-IN")}` : ` — Zone ${l.zone}`}</option>
          ))}
        </select>
      </Field>

      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Preferred visit date">
          <input type="date" min={today} value={form.visit_date} onChange={(e) => set("visit_date", e.target.value)} className="mt-2 w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900" data-testid="visit-date-input" />
        </Field>
        {kerala && loc?.stay_needed && (
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Nights of stay (₹{rates.stay_per_night.toLocaleString("en-IN")}/night)</span>
            <Stepper value={form.nights} min={0} max={14} onChange={(v) => set("nights", v)} testId="visit-nights" />
            <p className="mt-1 text-[11px] text-stone-500">Leave at 0 if you arrange our stay.</p>
          </div>
        )}
        {!kerala && loc && (
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Days on site (min {loc.min_days})</span>
            <Stepper value={Math.max(form.days, loc.min_days)} min={loc.min_days} max={30} onChange={(v) => set("days", v)} testId="visit-days" />
            <p className="mt-1 text-[11px] text-stone-500">Travel days count as working days.</p>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <Toggle on={form.urgent} onClick={() => set("urgent", !form.urgent)} label="Urgent / Sunday / holiday" sub="+25% on the visit fee" testId="visit-urgent-toggle" />
        <Toggle on={form.repeat} onClick={() => set("repeat", !form.repeat)} label="Repeat visit within 30 days" sub="−15%, same project" testId="visit-repeat-toggle" />
      </div>
    </div>
  );
};

export const DetailsStep = ({ form, setForm }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const cls = "mt-2 w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-900";
  return (
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Your name"><input required value={form.name} onChange={set("name")} className={cls} placeholder="Full name" data-testid="visit-name-input" /></Field>
      <Field label="Phone (WhatsApp)"><input required type="tel" value={form.phone} onChange={set("phone")} className={cls} placeholder="+91" data-testid="visit-phone-input" /></Field>
      <Field label="Email for the receipt"><input required type="email" value={form.email} onChange={set("email")} className={cls} placeholder="you@example.com" data-testid="visit-email-input" /></Field>
      <Field label="Site address / landmark (optional)"><input value={form.site_address} onChange={set("site_address")} className={cls} placeholder="Town, landmark or Google Maps pin" data-testid="visit-address-input" /></Field>
    </div>
  );
};
